import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GrowthWidget from "@/components/GrowthWidget";
import { FileText, Plus, Folder, Bell, Shield, ChevronRight, FileCheck, Clock, AlertCircle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/claims").then((r) => setClaims(r.data)).catch(() => setClaims([])),
      api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([])),
    ]).finally(() => setLoading(false));
  }, []);

  const active = claims.filter((c) => c.current_status !== "Claim Closed");
  const closed = claims.filter((c) => c.current_status === "Claim Closed");

  const promoteSelf = async () => {
    try {
      await api.post("/dev/promote-self");
      window.location.reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to promote");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F8FAFC]" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] mb-2">Customer Dashboard</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Welcome, {user?.name?.split(" ")[0]} 👋
            </h1>
            <p className="text-slate-600 mt-1">Here's a snapshot of your claims and documents.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={promoteSelf}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 underline"
              data-testid="dev-promote-self"
              title="Become admin if no admin exists"
            >
              Become first admin
            </button>
            <Link
              to="/file-claim"
              className="inline-flex items-center gap-2 bg-[#003DA5] hover:bg-[#002F80] text-white rounded-xl px-5 py-3 font-semibold transition-all shadow-lg shadow-blue-100"
              data-testid="dashboard-new-claim"
            >
              <Plus className="w-4 h-4" /> New Claim
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-active-claims" icon={Clock} color="#FF7A00" label="Active Claims" value={active.length} />
          <StatCard testid="stat-closed-claims" icon={FileCheck} color="#00A86B" label="Closed Claims" value={closed.length} />
          <StatCard testid="stat-documents" icon={Folder} color="#003DA5" label="Documents" value={docs.length} />
          <StatCard testid="stat-notifications" icon={Bell} color="#3B82F6" label="Notifications" value={0} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Active claims */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-heading font-bold text-lg text-slate-900">Your Claims</h2>
                <Link to="/track" className="text-xs font-semibold text-[#003DA5] hover:underline">Public tracker →</Link>
              </div>

              {loading ? (
                <div className="p-10 text-center text-slate-500">Loading…</div>
              ) : claims.length === 0 ? (
                <div className="p-10 text-center" data-testid="no-claims-state">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-semibold mb-3">No claims yet</p>
                  <p className="text-sm text-slate-500 mb-4">Start by filing your first claim — takes under 3 minutes.</p>
                  <Link to="/file-claim" className="inline-flex items-center gap-2 bg-[#FF7A00] text-white rounded-xl px-5 py-2.5 font-semibold">
                    File a Claim <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {claims.slice(0, 8).map((c) => (
                    <li
                      key={c.claim_id}
                      className="px-6 py-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between gap-4"
                      onClick={() => navigate(`/claim/${c.claim_id}`)}
                      data-testid={`claim-row-${c.claim_id}`}
                    >
                      <div>
                        <div className="font-mono text-sm font-bold text-slate-900">{c.claim_id}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {c.vehicle?.make} {c.vehicle?.model} · {c.vehicle?.reg_no} · {c.insurance?.insurer}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#E6F0FF] text-[#003DA5]">
                          {c.current_status}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Side panel */}
          <div className="space-y-6">
            <GrowthWidget />

            <div className="bg-gradient-to-br from-[#003DA5] to-[#0050D1] rounded-2xl p-6 text-white relative overflow-hidden">
              <Shield className="absolute top-4 right-4 w-20 h-20 text-white/10" />
              <div className="text-xs font-bold uppercase tracking-wider opacity-80">Document Vault</div>
              <div className="font-heading font-extrabold text-3xl mt-1">{docs.length}</div>
              <div className="text-sm opacity-90 mt-1">Securely stored documents</div>
              <Link to="/vault" className="mt-4 inline-flex items-center gap-2 bg-white text-[#003DA5] rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-50" data-testid="open-vault">
                Open Vault <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-[#FF7A00]" />
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Emergency</div>
              </div>
              <p className="text-sm text-slate-600 mb-3">Had an accident? One tap and our team is on it.</p>
              <Link to="/emergency" className="block text-center bg-[#FF7A00] hover:bg-[#CC6200] text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors" data-testid="emergency-shortcut">
                Get Emergency Help
              </Link>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Quick Actions</div>
              <div className="space-y-2">
                <Link to="/file-claim" className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold text-slate-800">New Claim →</Link>
                <Link to="/track" className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold text-slate-800">Track Claim →</Link>
                <Link to="/knowledge" className="block px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-semibold text-slate-800">Knowledge Center →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 hover-lift" data-testid={testid}>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
        style={{ background: `${color}15`, color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-heading font-extrabold text-2xl text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
